import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Badge, Form, Spinner, Modal } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { getToken, getUserId } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import { calculateGrade, isWithdrawn, isTransferred, inactiveLabel } from "../utils/teachingUtils";
import * as XLSX from "xlsx";

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
// ช่องกรอกคะแนนเป็น type="text" ไม่ใช่ "number" เพราะลูกกลิ้งเมาส์เลื่อนค่าใน input number
// ได้เองตอนเลื่อนหน้าจอ คะแนนจึงเปลี่ยนโดยครูไม่รู้ตัว — ย้ายมาคุมรูปแบบเองแทน
// ยอมให้ค่าค้างระหว่างพิมพ์ ("", "12.") ส่วนตัวเลขที่ใช้ได้จริงตรวจอีกครั้งตอนกดบันทึก
const isScoreInput = (v) => /^\d*\.?\d{0,2}$/.test(v);
const parseScore = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const StudentAvatar = ({ photo, name }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;

  const circleStyle = { width: 28, height: 28, borderRadius: '50%', flexShrink: 0 };
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: 34, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

// การ์ดกรอกคะแนนนักเรียน 1 คน สำหรับมุมมองมือถือ
const StudentScoreCard = ({ st, index, savedItems, marks, onMarkChange, total, grade, waiting }) => {
  // คนที่จำหน่ายแล้ว (st_status = 0) ยังกรอกคะแนนได้ — เทอมที่เขาออกกลางคันก็มีคะแนนเก็บของเขาอยู่
  // ปิดช่องกรอกเฉพาะคนที่ย้ายห้อง เพราะคะแนนของเขาไปอยู่กับห้องใหม่แล้ว (กฎเดียวกับ GradeEntry)
  const transferred = isTransferred(st);
  return (
    <div className={`border rounded p-3 ${isWithdrawn(st) ? 'bg-danger-subtle' : isTransferred(st) ? 'bg-warning-subtle' : ''}`}>
      <div className="d-flex align-items-center gap-2">
        <StudentAvatar photo={st.st_photo} name={st.st_name} />
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold text-truncate">{st.st_name}</div>
          <div className="fs-xs text-muted">#{index + 1} · {st.st_idnumber}</div>
        </div>
      </div>
      {transferred ? (
        <div className="mt-2 pt-2 border-top text-center fs-sm fw-semibold text-muted">
          {inactiveLabel(st)}
        </div>
      ) : (
        <>
          <div className="mt-2 pt-2 border-top d-flex flex-wrap gap-2">
            {savedItems.map((it) => (
              <div key={it.si_id} style={{ width: 90 }}>
                <div className="fs-xs text-muted text-truncate" title={it.si_name}>{it.si_name} ({it.si_maxscore})</div>
                <Form.Control
                  size="sm"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="text-center"
                  value={marks[`${st.study_id}_${it.si_id}`] ?? ""}
                  onChange={(e) => onMarkChange(st.study_id, it.si_id, e.target.value)}
                />
              </div>
            ))}
          </div>
          <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top fs-sm">
            <div><span className="text-muted">รวม:</span> <strong style={{ color: "blue" }}>{total !== null ? total : "-"}</strong></div>
            <div>
              {waiting
                ? <Badge bg="secondary">ร (รอผล)</Badge>
                : grade !== null
                  ? <Badge bg={grade >= 2 ? "success" : grade >= 1 ? "warning" : "danger"}>{grade}</Badge>
                  : "-"}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function ScoreTable() {
  const { teach_id } = useParams();
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === "dark") {
      const btnWhite = document.getElementsByClassName("btn-white");
      for (const btn of btnWhite) {
        btn.classList.add("btn-outline-primary");
        btn.classList.remove("btn-white");
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName("btn-outline-primary");
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove("btn-outline-primary");
        btn.classList.add("btn-white");
      }
    }
  };

  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  // ---- Page state ----
  const [room, setRoom] = useState("");
  const [subject, setSubject] = useState("");
  const [students, setStudents] = useState([]);
  // items: [{ si_id|null, si_name, si_maxscore, si_order }]
  const [items, setItems] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  // marks: { `${study_id}_${si_id}`: '' | number-string }
  const [marks, setMarks] = useState({});
  const [savingItems, setSavingItems] = useState(false);
  const [savingMarks, setSavingMarks] = useState(false);
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  // ---- Copy template from another room ----
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);

  const loadSheet = () => {
    setLoading(true);
    axiosInstance
      .get(`${process.env.REACT_APP_API}/getScoreSheet/${teach_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        const { items, students, marks } = res.data;
        if (students.length > 0) {
          const first = students[0];
          setRoom(first.tb_class_c_id + "/" + first.r_no);
          setSubject(first.sub_subid + " " + first.sub_subname);
        }
        setStudents(students);
        setItems(items.map((it) => ({ ...it, si_maxscore: String(it.si_maxscore) })));
        const map = {};
        marks.forEach((m) => {
          map[`${m.study_id}_${m.si_id}`] = m.sm_score === null ? "" : String(m.sm_score);
        });
        setMarks(map);
        setDeletedIds([]);
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: error.response?.data?.title || "เกิดข้อผิดพลาด",
          text: error.response?.data?.message || "ไม่สามารถโหลดข้อมูลได้",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(loadSheet, [teach_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Component template editing ----
  const totalMax = items.reduce((sum, it) => sum + (parseFloat(it.si_maxscore) || 0), 0);
  const roundedTotal = Math.round(totalMax * 100) / 100;

  const handleItemChange = (index, field, value) => {
    const v = field === "si_maxscore" ? value.trim() : value;
    if (field === "si_maxscore" && !isScoreInput(v)) return; // ปัดตัวอักษรทิ้งตั้งแต่ตอนพิมพ์
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, [field]: v } : it)));
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      { si_id: null, si_name: "", si_maxscore: "", si_type: "normal", si_order: prev.length + 1 },
    ]);
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => {
      const target = prev[index];
      if (target.si_id) setDeletedIds((d) => [...d, target.si_id]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSaveItems = () => {
    if (items.some((it) => !(it.si_name || "").trim())) {
      return Swal.fire({ icon: "warning", title: "กรุณากรอกชื่อรายการให้ครบ" });
    }
    if (roundedTotal !== 100) {
      return Swal.fire({ icon: "warning", title: "คะแนนเต็มรวมกันต้องเท่ากับ 100", text: `ขณะนี้รวม ${roundedTotal}` });
    }
    setSavingItems(true);
    axiosInstance
      .post(
        `${process.env.REACT_APP_API}/saveScoreItems`,
        {
          teach_id,
          items: items.map((it, i) => ({
            si_id: it.si_id,
            si_name: it.si_name.trim(),
            si_maxscore: parseFloat(it.si_maxscore) || 0,
            si_type: it.si_type || "normal",
            si_order: i + 1,
          })),
          deletedIds,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      )
      .then(() => {
        Swal.fire({ title: "บันทึกตารางคะแนนสำเร็จ", icon: "success", timer: 1200, showConfirmButton: false });
        loadSheet();
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: err.response?.data?.message || "ไม่สามารถบันทึกได้",
        });
      })
      .finally(() => setSavingItems(false));
  };

  // Open the modal and load sibling rooms (same subject) that have a template
  const openCopyModal = () => {
    setShowCopyModal(true);
    setLoadingSources(true);
    axiosInstance
      .get(`${process.env.REACT_APP_API}/getScoreItemSources/${teach_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => setSources(res.data))
      .catch((err) => {
        setShowCopyModal(false);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: err.response?.data?.message || "ไม่สามารถโหลดรายการห้องได้",
        });
      })
      .finally(() => setLoadingSources(false));
  };

  // Pull the chosen room's template into the local design table (as new items).
  // The teacher still reviews and presses "บันทึกตารางคะแนน" to persist.
  const handleCopyFromSource = (src) => {
    const applyCopy = () => {
      axiosInstance
        .get(`${process.env.REACT_APP_API}/getScoreItems/${src.teach_id}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        })
        .then((res) => {
          const copied = res.data.map((it, i) => ({
            si_id: null,
            si_name: it.si_name,
            si_maxscore: String(it.si_maxscore),
            si_type: it.si_type || "normal",
            si_order: i + 1,
          }));
          // Replace the current template: queue any persisted items for deletion
          setDeletedIds((prev) => [
            ...prev,
            ...items.filter((it) => it.si_id).map((it) => it.si_id),
          ]);
          setItems(copied);
          setShowCopyModal(false);
          Swal.fire({
            icon: "info",
            title: "คัดลอกตารางคะแนนแล้ว",
            text: 'กรุณาตรวจสอบและกด "บันทึกตารางคะแนน" เพื่อยืนยัน',
            timer: 2200,
            showConfirmButton: false,
          });
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "เกิดข้อผิดพลาด",
            text: err.response?.data?.message || "ไม่สามารถคัดลอกได้",
          });
        });
    };

    if (items.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "แทนที่ตารางคะแนนปัจจุบัน?",
        text: "รายการคะแนนที่มีอยู่ (รวมถึงคะแนนที่กรอกไว้) จะถูกแทนที่เมื่อบันทึก",
        showCancelButton: true,
        confirmButtonText: "แทนที่",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#d33",
      }).then((result) => {
        if (result.isConfirmed) applyCopy();
      });
    } else {
      applyCopy();
    }
  };

  // ---- Score grid ----
  const handleMarkChange = (study_id, si_id, value) => {
    const v = value.trim();
    if (!isScoreInput(v)) return; // ไม่ใช่ตัวเลข = ไม่รับ ช่องคงค่าเดิมไว้
    setMarks((prev) => ({ ...prev, [`${study_id}_${si_id}`]: v }));
  };

  const rowTotal = (study_id) => {
    let sum = 0;
    let hasAny = false;
    items.forEach((it) => {
      if (!it.si_id) return;
      const v = marks[`${study_id}_${it.si_id}`];
      if (v !== "" && v !== undefined && v !== null) {
        sum += parseFloat(v) || 0;
        hasAny = true;
      }
    });
    return hasAny ? Math.round(sum * 100) / 100 : null;
  };

  const savedItems = items.filter((it) => it.si_id); // only persisted components can hold marks
  // ย้ายห้องแล้วไม่ต้องกรอก (คะแนนอยู่กับห้องใหม่) ส่วนคนที่จำหน่ายแล้วยังกรอก/บันทึกได้ตามปกติ
  const scorableStudents = students.filter((st) => !isTransferred(st));

  // g_waiting preview: ตราบใดที่ยังกรอกคะแนนไม่ครบทุกช่อง นักเรียนคนนั้นจะได้ ร แทนเกรด
  // เมื่อบันทึก — สะท้อนกฎเดียวกับ saveScoreMarks ฝั่ง server ไว้ตรงนี้
  const hasMark = (study_id, si_id) => {
    const v = marks[`${study_id}_${si_id}`];
    return v !== "" && v !== undefined && v !== null;
  };
  const anyEntered = savedItems.some(
    (it) => scorableStudents.some((st) => hasMark(st.study_id, it.si_id))
  );
  const rowIncomplete = (study_id) => savedItems.some((it) => !hasMark(study_id, it.si_id));

  const handleSaveMarks = () => {
    // Guard: ทุกช่องที่กรอกไว้ต้องเป็นตัวเลข และไม่เกินคะแนนเต็มของรายการนั้น
    for (const it of savedItems) {
      const max = parseFloat(it.si_maxscore) || 0;
      for (const st of scorableStudents) {
        const v = marks[`${st.study_id}_${it.si_id}`];
        if (v === "" || v === undefined || v === null) continue;
        const score = parseScore(v);
        if (score === null) {
          return Swal.fire({
            icon: "warning",
            title: "คะแนนต้องเป็นตัวเลข",
            text: `"${it.si_name}" ของ ${st.st_name} กรอกไว้ว่า "${v}"`,
          });
        }
        if (score > max) {
          return Swal.fire({
            icon: "warning",
            title: "คะแนนเกินคะแนนเต็ม",
            text: `"${it.si_name}" กำหนดเต็ม ${max} คะแนน (${st.st_name} กรอก ${score})`,
          });
        }
      }
    }

    const payload = [];
    scorableStudents.forEach((st) => {
      savedItems.forEach((it) => {
        const key = `${st.study_id}_${it.si_id}`;
        if (key in marks) {
          payload.push({ study_id: st.study_id, si_id: it.si_id, score: marks[key] });
        }
      });
    });

    setSavingMarks(true);
    axiosInstance
      .post(
        `${process.env.REACT_APP_API}/saveScoreMarks`,
        { teach_id, marks: payload, update_by: String(getUserId()) },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      )
      .then(() => {
        Swal.fire({ title: "บันทึกคะแนนสำเร็จ", icon: "success", timer: 1200, showConfirmButton: false });
        // Reflect the synced totals into g_grade shown here
        setStudents((prev) => prev.map((st) => ({ ...st, g_grade: rowTotal(st.study_id) })));
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: err.response?.data?.message || "ไม่สามารถบันทึกได้",
        });
      })
      .finally(() => setSavingMarks(false));
  };

  // ---- Export / Print ----
  const pageTitle = `ตารางคะแนน วิชา ${subject} ชั้น ม.${room}`;
  const fileBase = `ตารางคะแนน_${(subject || teach_id).split(" ")[0]}_ม.${room}`.replace(/[\\/:*?"<>|]/g, "-");
  const escHtml = (str) => String(str ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // ไฟล์ Excel เว้นคอลัมน์ "รวม" และ "ผลการเรียน" ไว้ให้ครูกรอกเอง
  // ส่วนใบพิมพ์เติม "รวม" ให้เมื่อคะแนนเก็บครบทุกช่องแล้ว — สอบปลายภาค (si_type = final) ยังว่างได้
  // เพราะครูมักพิมพ์ใบนี้ก่อนสอบปลายภาค พอมีคะแนนปลายภาคแล้วพิมพ์ใหม่จะบวกเข้าไปด้วยเอง
  // "ผลการเรียน" ยังเว้นว่างไว้เหมือนเดิม เพราะยอดที่ยังไม่รวมปลายภาคตัดเกรดไม่ได้
  const printTotal = (study_id) => {
    if (savedItems.length === 0) return "";
    let sum = 0;
    let hasAny = false;
    for (const it of savedItems) {
      const v = marks[`${study_id}_${it.si_id}`];
      if (v !== "" && v !== undefined && v !== null) {
        sum += parseFloat(v) || 0;
        hasAny = true;
      } else if ((it.si_type || "normal") !== "final") {
        return ""; // คะแนนเก็บยังไม่ครบ = ยังรวมให้ไม่ได้
      }
    }
    return hasAny ? String(Math.round(sum * 100) / 100) : "";
  };

  const handleExportExcel = () => {
    const rows = [
      [pageTitle],
      [`ส่งออก: ${new Date().toLocaleString("th-TH")}`],
      [],
      [
        "เลขที่", "เลขประจำตัว", "ชื่อ-สกุล",
        ...savedItems.map((it) => `${it.si_name} (${it.si_maxscore})`),
        "รวม", "ผลการเรียน",
      ],
    ];
    students.forEach((st, index) => {
      if (isTransferred(st)) {
        rows.push([
          index + 1, st.st_idnumber, `${st.st_name} (${inactiveLabel(st)})`,
          ...savedItems.map(() => ""),
          "", "",
        ]);
        return;
      }
      rows.push([
        index + 1, st.st_idnumber,
        isWithdrawn(st) ? `${st.st_name} (${inactiveLabel(st)})` : st.st_name,
        ...savedItems.map((it) => {
          const v = marks[`${st.study_id}_${it.si_id}`];
          return v === "" || v === undefined || v === null ? "" : parseFloat(v);
        }),
        "", "",
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [
      { wch: 6 }, { wch: 9 }, { wch: 36 },
      ...savedItems.map(() => ({ wch: 12 })),
      { wch: 8 }, { wch: 10 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ตารางคะแนน");
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
  };

  const handlePrint = () => {
    const thStyle = "border:1px solid #999;padding:3px 4px;background:#ddeeff;text-align:center;";
    const tdStyle = "border:1px solid #999;padding:3px 4px;";
    const body = students.map((st, index) => {
      const rowBg = isWithdrawn(st) ? "background:#f8d7da;" : isTransferred(st) ? "background:#fff3cd;" : "";
      const head = `
        <td style="${tdStyle}${rowBg}text-align:center;">${index + 1}</td>
        <td style="${tdStyle}${rowBg}text-align:center;">${escHtml(st.st_idnumber)}</td>
        <td style="${tdStyle}${rowBg}">${escHtml(st.st_name)}</td>`;
      if (isTransferred(st)) {
        return `<tr>${head}
          <td style="${tdStyle}${rowBg}text-align:center;font-weight:bold;" colspan="${savedItems.length}">${escHtml(inactiveLabel(st))}</td>
          <td style="${tdStyle}${rowBg}"></td>
          <td style="${tdStyle}${rowBg}"></td>
        </tr>`;
      }
      const cells = savedItems.map((it) => {
        const v = marks[`${st.study_id}_${it.si_id}`];
        const text = v === "" || v === undefined || v === null ? "" : v;
        return `<td style="${tdStyle}${rowBg}text-align:center;">${escHtml(text)}</td>`;
      }).join("");
      return `<tr>${head}${cells}
        <td style="${tdStyle}${rowBg}text-align:center;">${escHtml(printTotal(st.study_id))}</td>
        <td style="${tdStyle}${rowBg}"></td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${escHtml(pageTitle)}</title>
      <style>
        body { font-family: 'Cordia New', Tahoma, Arial, sans-serif; font-size: 15px; padding: 10px; }
        table { border-collapse: collapse; width: 100%; }
      </style></head>
      <body onload="window.print()">
        <h3 style="text-align:center;margin-bottom:12px;">${escHtml(pageTitle)}</h3>
        <table><thead><tr>
          <th style="${thStyle}width:35px;">เลขที่</th>
          <th style="${thStyle}width:60px;font-size:13px;word-break:break-word;">เลขประจำตัว</th>
          <th style="${thStyle}min-width:200px;">ชื่อ-สกุล</th>
          ${savedItems.map((it) => `<th style="${thStyle}">${escHtml(it.si_name)}<br><small>(${escHtml(it.si_maxscore)})</small></th>`).join("")}
          <th style="${thStyle}width:60px;">รวม</th>
          <th style="${thStyle}width:80px;">ผลการเรียน</th>
        </tr></thead>
        <tbody>${body || `<tr><td style="${tdStyle}text-align:center;" colspan="${savedItems.length + 5}">ไม่พบข้อมูล</td></tr>`}</tbody>
        </table>
      </body></html>`;
    const win = window.open("", "_blank", "width=1200,height=900");
    win.document.write(html);
    win.document.close();
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ข้อมูลการสอน</Link></li>
              <li className="breadcrumb-item"><Link to="/teaching/saveGrade">บันทึกผลการเรียน</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ตารางคะแนน</li>
            </ol>
            <h4 className="main-title mb-0">
              ตารางคะแนน — วิชา: <u>{subject}</u>&ensp;ชั้น <u>ม.{room}</u>
            </h4>
          </div>
        </div>

        {/* ---- Section 1: ออกแบบตารางคะแนน ---- */}
        <Row className="g-3 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6">ออกแบบตารางคะแนน</Card.Title>
                <Badge bg={roundedTotal === 100 ? "success" : "danger"} className="fs-sm">
                  รวม {roundedTotal} / 100
                </Badge>
              </Card.Header>
              <Card.Body className="p-3">
                <Table responsive className="mb-3" style={{ maxWidth: "640px" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "60px" }} className="text-center">ลำดับ</th>
                      <th>ชื่อรายการ</th>
                      <th style={{ width: "140px" }} className="text-center">คะแนนเต็ม</th>
                      <th style={{ width: "150px" }} className="text-center">ประเภท</th>
                      <th style={{ width: "60px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length > 0 ? items.map((it, index) => (
                      <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td>
                          <Form.Control
                            size="sm"
                            value={it.si_name}
                            placeholder="เช่น ใบงานที่ 1 / สอบกลางภาค"
                            onChange={(e) => handleItemChange(index, "si_name", e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            className="text-center"
                            value={it.si_maxscore}
                            onChange={(e) => handleItemChange(index, "si_maxscore", e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={it.si_type || "normal"}
                            onChange={(e) => handleItemChange(index, "si_type", e.target.value)}
                          >
                            <option value="normal">คะแนนเก็บ</option>
                            <option value="midterm">สอบกลางภาค</option>
                            <option value="final">สอบปลายภาค</option>
                          </Form.Select>
                        </td>
                        <td className="text-center">
                          <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => handleRemoveItem(index)}>
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={5} className="text-center text-muted">ยังไม่มีรายการคะแนน</td></tr>
                    )}
                  </tbody>
                </Table>
                <div className="d-flex flex-wrap gap-2">
                  <Button variant="white" size="sm" onClick={handleAddItem}>
                    <i className="ri-add-line"></i> เพิ่มรายการ
                  </Button>
                  <Button variant="outline-primary" size="sm" onClick={openCopyModal}>
                    <i className="ri-file-copy-line"></i> ดึงจากห้องอื่น
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleSaveItems}
                    disabled={savingItems || roundedTotal !== 100 || items.length === 0}
                  >
                    {savingItems ? <Spinner animation="border" size="sm" /> : <><i className="ri-check-line"></i> บันทึกตารางคะแนน</>}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ---- Section 2: กรอกคะแนน ---- */}
        <Row className="g-3 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                <Card.Title as="h6" className="mb-0">กรอกคะแนน</Card.Title>
                {savedItems.length > 0 && (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Button variant="outline-secondary" size="sm" onClick={handlePrint} disabled={students.length === 0}>
                      <i className="ri-printer-line me-1"></i>พิมพ์
                    </Button>
                    <Button variant="outline-success" size="sm" onClick={handleExportExcel} disabled={students.length === 0}>
                      <i className="ri-file-excel-line me-1"></i>Export Excel
                    </Button>
                    <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                      <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                        onClick={() => setView('table')} title="มุมมองตาราง">
                        <i className="ri-table-line" />
                      </button>
                      <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                        onClick={() => setView('card')} title="มุมมองการ์ด">
                        <i className="ri-layout-grid-line" />
                      </button>
                    </div>
                  </div>
                )}
              </Card.Header>
              <Card.Body className="p-3">
                {savedItems.length === 0 ? (
                  <p className="text-muted mb-0">กรุณาบันทึกตารางคะแนน (รวม 100) ก่อนจึงจะกรอกคะแนนได้</p>
                ) : view === 'card' ? (
                  <>
                    <Row className="g-3">
                      {students.map((st, index) => {
                        const total = rowTotal(st.study_id);
                        const grade = calculateGrade(total);
                        const waiting = anyEntered && rowIncomplete(st.study_id);
                        return (
                          <Col key={st.study_id} xs={12} md={6} xl={4}>
                            <StudentScoreCard st={st} index={index} savedItems={savedItems} marks={marks}
                              onMarkChange={handleMarkChange} total={total} grade={grade} waiting={waiting} />
                          </Col>
                        );
                      })}
                    </Row>
                    <div className="mt-3 d-flex justify-content-end">
                      <Button variant="success" size="sm" onClick={handleSaveMarks} disabled={savingMarks}>
                        {savingMarks ? <Spinner animation="border" size="sm" /> : <><i className="ri-check-line"></i> บันทึกคะแนน</>}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Table responsive hover className="mb-0" style={{ whiteSpace: "nowrap" }}>
                      <thead>
                        <tr>
                          <th className="text-center" style={{ width: "50px" }}>เลขที่</th>
                          <th className="text-center" style={{ width: "110px" }}>เลขประจำตัว</th>
                          <th>ชื่อ-สกุล</th>
                          {savedItems.map((it) => (
                            <th key={it.si_id} className="text-center">
                              {it.si_name}<br /><small className="text-muted">({it.si_maxscore})</small>
                            </th>
                          ))}
                          <th className="text-center" style={{ width: "80px" }}>รวม</th>
                          <th className="text-center" style={{ width: "90px" }}>ผลการเรียน</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((st, index) => {
                          const total = rowTotal(st.study_id);
                          const grade = calculateGrade(total);
                          const waiting = anyEntered && rowIncomplete(st.study_id);
                          const transferred = isTransferred(st);
                          return (
                            <tr key={st.study_id} className={isWithdrawn(st) ? "table-danger" : isTransferred(st) ? "table-warning" : ""}>
                              <th scope="row" className="text-center">{index + 1}</th>
                              <td className="text-center">{st.st_idnumber}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <StudentAvatar photo={st.st_photo} name={st.st_name} />
                                  <span>{st.st_name}</span>
                                </div>
                              </td>
                              {transferred ? (
                                <td colSpan={savedItems.length + 2} className="text-center fw-semibold">
                                  {inactiveLabel(st)}
                                </td>
                              ) : (<>
                              {savedItems.map((it) => (
                                <td key={it.si_id} className="text-center">
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    className="text-center"
                                    style={{ width: "70px", margin: "0 auto" }}
                                    value={marks[`${st.study_id}_${it.si_id}`] ?? ""}
                                    onChange={(e) => handleMarkChange(st.study_id, it.si_id, e.target.value)}
                                  />
                                </td>
                              ))}
                              <td className="text-center fw-semibold" style={{ color: "blue" }}>
                                {total !== null ? total : "-"}
                              </td>
                              <td className="text-center">
                                {waiting
                                  ? <Badge bg="secondary">ร (รอผล)</Badge>
                                  : grade !== null
                                    ? <Badge bg={grade >= 2 ? "success" : grade >= 1 ? "warning" : "danger"}>{grade}</Badge>
                                    : "-"}
                              </td>
                              </>)}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    <div className="mt-3 d-flex justify-content-end">
                      <Button variant="success" size="sm" onClick={handleSaveMarks} disabled={savingMarks}>
                        {savingMarks ? <Spinner animation="border" size="sm" /> : <><i className="ri-check-line"></i> บันทึกคะแนน</>}
                      </Button>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="mb-4 d-flex justify-content-end">
          <Link to="/teaching/saveGrade" className="btn btn-secondary btn-sm">
            <i className="ri-arrow-left-line"></i> ย้อนกลับ
          </Link>
        </div>

        {/* ---- Modal: ดึงตารางคะแนนจากห้องอื่น ---- */}
        <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title as="h6" className="mb-0">ดึงตารางคะแนนจากห้องอื่น</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="fs-sm text-muted">
              เลือกห้องที่สอนวิชาเดียวกันและมีตารางคะแนนอยู่แล้ว เพื่อคัดลอกรายการคะแนนมาใช้
            </p>
            {loadingSources ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
            ) : sources.length === 0 ? (
              <p className="text-muted mb-0">ไม่พบห้องอื่นที่สอนวิชานี้และมีตารางคะแนนแล้ว</p>
            ) : (
              <div className="list-group">
                {sources.map((src) => (
                  <button
                    key={src.teach_id}
                    type="button"
                    className="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                    onClick={() => handleCopyFromSource(src)}
                  >
                    <span>
                      <strong>ม.{src.tb_class_c_id}/{src.r_no}</strong>
                      <span className="text-muted ms-2 fs-sm">{src.sub_subid} {src.sub_subname}</span>
                    </span>
                    <Badge bg="secondary">{src.item_count} รายการ</Badge>
                  </button>
                ))}
              </div>
            )}
          </Modal.Body>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
